import React, { useState, useRef } from 'react'
import { Camera, Upload, X, User } from 'lucide-react'
import apiService from '../services/api'

interface ProfilePictureUploadProps {
  currentAvatar?: string
  onAvatarChange?: (avatarUrl: string) => void
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  currentAvatar,
  onAvatarChange,
  size = 'md',
  className = ''
}) => {
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32'
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.')
      return
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024 // 5MB
    if (file.size > maxSize) {
      setError('File size too large. Maximum size is 5MB.')
      return
    }

    setError(null)
    
    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      setPreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)

    // Upload file
    uploadProfilePicture(file)
  }

  const uploadProfilePicture = async (file: File) => {
    setIsUploading(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await apiService.post('/api/upload/profile-picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }) as any

      if (response.success) {
        onAvatarChange?.(response.data.avatarUrl)
        setPreview(null) // Clear preview since we now have the real URL
      } else {
        setError(response.message || 'Failed to upload profile picture')
      }
    } catch (error: any) {
      console.error('Profile picture upload error:', error)
      setError(error.response?.data?.message || 'Failed to upload profile picture')
    } finally {
      setIsUploading(false)
    }
  }

  const handleDelete = async () => {
    setIsUploading(true)
    setError(null)

    try {
      const response = await apiService.delete('/api/upload/profile-picture') as any

      if (response.success) {
        onAvatarChange?.('')
        setPreview(null)
      } else {
        setError(response.message || 'Failed to delete profile picture')
      }
    } catch (error: any) {
      console.error('Profile picture delete error:', error)
      setError(error.response?.data?.message || 'Failed to delete profile picture')
    } finally {
      setIsUploading(false)
    }
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  const displayImage = preview || currentAvatar

  return (
    <div className={`relative ${className}`}>
      {/* Profile Picture Display */}
      <div className={`${sizeClasses[size]} relative rounded-full overflow-hidden border-2 border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800`}>
        {displayImage ? (
          <img
            src={displayImage}
            alt="Profile"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User className="w-1/2 h-1/2 text-gray-400" />
          </div>
        )}

        {/* Upload Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200">
          <button
            onClick={handleClick}
            disabled={isUploading}
            className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
            title="Upload profile picture"
          >
            {isUploading ? (
              <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
            ) : (
              <Camera className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-2 flex gap-2 justify-center">
        <button
          onClick={handleClick}
          disabled={isUploading}
          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-1"
        >
          <Upload className="w-3 h-3" />
          {currentAvatar ? 'Change' : 'Upload'}
        </button>

        {currentAvatar && (
          <button
            onClick={handleDelete}
            disabled={isUploading}
            className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Delete
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-2 text-xs text-red-600 text-center">
          {error}
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  )
}

export default ProfilePictureUpload
